/* eslint-disable react-hooks/exhaustive-deps */
import {SyntheticEvent, useCallback, useEffect, useMemo, useState} from 'react';

import {t} from 'i18next';
import {useDispatch} from 'react-redux';

import {CarouselAnalyticsProps} from 'components/recommendations/carousel-list-container/types';
import {AS_HALF_PRODUCT_QUANTITY} from 'constants/recommendation';
import useAnalytics, {
  ANALYTICS,
  BASKET_PDP_ATTRIBUTES,
  BASKET_UPDATE_ITEM_SOURCE_EVENTS,
  BASKET_UPDATE_ITEM_TRIGGER_TYPE,
  EVENT_SOURCE,
  SUGGESTEDLIST_CHANNELS
} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {updatePreviewItemQuantityAction} from 'store/actions/basket-action';
import {Product, ProductSearchResult} from 'types';
import {getProductIdWithoutPrefix, marketingAnalytics, productAnalytics} from 'utils';
import {debounce, filter1PItems, filter3PItems} from 'utils/helper';
import {getBasketRecommendationAnalyticTitle} from 'utils/recommendation-utils/recommendations';

import {useCurrentBasketItem} from '../useCurrentBasketItem';

const INCREMENT_DECREMENT_DEBOUNCE_INTERVAL = 300;

interface UseCounterProps {
  initialCount?: number;
  enablePartialQuantity?: boolean;
  product: Product | ProductSearchResult;
  indexClicked?: number;
  onUpdateBasket?: () => void;
  closePicker?: () => void;
  carouselAnalyticsProps?: CarouselAnalyticsProps;
  categoryAnalytics?: {
    categoryName?: string;
  };
  sendCarouselImpressionAnalytics: () => void;
}

export const useCounter = ({
  initialCount = 0,
  enablePartialQuantity = false,
  product,
  indexClicked,
  onUpdateBasket,
  closePicker,
  carouselAnalyticsProps,
  categoryAnalytics,
  sendCarouselImpressionAnalytics
}: UseCounterProps) => {
  const dispatch = useDispatch();
  const analytics = useAnalytics();
  const [count, setCount] = useState<number>(initialCount);
  const [inProgress, setProgress] = useState(false);
  const idToken = useAppSelector((state) => state?.auth?.idToken);
  const {isLoading: loading, basket} = useAppSelector((state) => state?.basket);
  const {recommendations} = useAppSelector((state) => state.recommendations);
  const variantId = (product?.variants.length && product?.variants[0].id) ?? '';
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const carouselName = `${t(getBasketRecommendationAnalyticTitle(recommendations.recommendationType ?? ''))}`;

  const productForHook = useMemo(() => {
    if ('variants' in product && Array.isArray(product.variants)) {
      return product as Product;
    }
    return product as ProductSearchResult;
  }, [product]);

  const {basketItem, productCount} = useCurrentBasketItem(productForHook);
  const storeBasketItemData = {
    productId: product?.id || '',
    sourceEvent: BASKET_PDP_ATTRIBUTES.BASKET_RECOMMENDATION_V2,
    algorithm: recommendations?.algorithm || '',
    channel: isReactNativeView ? SUGGESTEDLIST_CHANNELS.MKP_APP : SUGGESTEDLIST_CHANNELS.MKP_WEB,
    carouselName
  };

  const preventDefault = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();
    e.nativeEvent?.stopImmediatePropagation?.();
  }, []);

  const analyticsData = useMemo(
    () => ({
      eventName: '',
      sourceEvent: '',
      triggerType: '',
      productId: basketItem?.productId || '',
      productPrice: basketItem?.productDetails?.displayPrice || '',
      qty: 0,
      carouselName,
      pageName: BASKET_PDP_ATTRIBUTES.PAGE_NAME
    }),
    [basketItem]
  );

  useEffect(() => {
    setCount(initialCount);
  }, [initialCount]);

  const incrementDebounced = useMemo(() => {
    return debounce(async (newCount: number) => {
      if (loading) return;

      try {
        setProgress(true);
        onUpdateBasket?.();

        const sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM;
        const analyticsToSend = {
          ...analyticsData,
          qty: newCount,
          triggerType: BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON,
          sourceEvent
        };

        dispatch(
          updatePreviewItemQuantityAction({
            productId: getProductIdWithoutPrefix(product?.id || ''),
            quantity: newCount,
            idToken,
            variantId
          })
        );

        if (analyticsToSend) {
          analytics.trackEvent(ANALYTICS.UPDATE_BASKET_ITEM, {
            ...analyticsToSend,
            basketId: basket?.id || 0,
            itemsCount: basket?.basketItems.length || 0,
            priceBeforeDiscount: basket?.prices?.totalOriginalPrice || 0,
            total: basket?.prices?.totalFinalPriceToPay || 0,
            discount: basket?.prices?.roundedDiscountAmount || 0,
            delivery: basket?.prices?.shippingFee || 0,
            storeId: basket?.storeCode || '',
            channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
            segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
            basketSize: basket?.basketItems?.length || 0,
            onePCount: filter1PItems(basket?.basketItems || []).length || 0,
            threePCount: filter3PItems(basket?.basketItems || []).length || 0
          });
        }
      } catch {
        setCount(count);
      } finally {
        setProgress(false);
      }
    }, INCREMENT_DECREMENT_DEBOUNCE_INTERVAL);
  }, [
    loading,
    updatePreviewItemQuantityAction,
    analyticsData,
    variantId,
    product,
    indexClicked,
    enablePartialQuantity,
    count,
    onUpdateBasket
  ]);

  const decrementDebounced = useMemo(() => {
    return debounce(async (newCount: number) => {
      if (loading) return;

      try {
        setProgress(true);
        onUpdateBasket?.();
        const sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM;
        const analyticsToSend = {
          ...analyticsData,
          qty: newCount,
          triggerType: BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON,
          sourceEvent
        };

        dispatch(
          updatePreviewItemQuantityAction({
            productId: getProductIdWithoutPrefix(product?.id || ''),
            quantity: newCount,
            idToken,
            variantId
          })
        );

        if (analyticsToSend) {
          analytics.trackEvent(ANALYTICS.UPDATE_BASKET_ITEM, {
            ...analyticsToSend,
            basketId: basket?.id || 0,
            itemsCount: basket?.basketItems.length || 0,
            priceBeforeDiscount: basket?.prices?.totalOriginalPrice || 0,
            total: basket?.prices?.totalFinalPriceToPay || 0,
            discount: basket?.prices?.roundedDiscountAmount || 0,
            delivery: basket?.prices?.shippingFee || 0,
            storeId: basket?.storeCode || '',
            channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
            segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
            basketSize: basket?.basketItems?.length || 0,
            onePCount: filter1PItems(basket?.basketItems || []).length || 0,
            threePCount: filter3PItems(basket?.basketItems || []).length || 0
          });
        }
      } catch {
        setCount(count);
      } finally {
        setProgress(false);
      }
    }, INCREMENT_DECREMENT_DEBOUNCE_INTERVAL);
  }, [
    loading,
    updatePreviewItemQuantityAction,
    analyticsData,
    variantId,
    product,
    indexClicked,
    enablePartialQuantity,
    count,
    onUpdateBasket
  ]);

  const onDecrement = useCallback(
    (e: SyntheticEvent) => {
      preventDefault(e);
      const decrement = enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : 1;
      const newCount = Math.max(0, count - decrement);
      setCount(newCount);
      sendCarouselImpressionAnalytics();
      decrementDebounced(newCount);
    },
    [count, enablePartialQuantity, decrementDebounced, preventDefault]
  );

  const onIncrement = useCallback(
    (e: SyntheticEvent) => {
      preventDefault(e);
      const increment = enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : 1;
      const newCount = count + increment;
      setCount(newCount);
      sendCarouselImpressionAnalytics();
      incrementDebounced(newCount);
    },
    [count, enablePartialQuantity, incrementDebounced, preventDefault]
  );

  const onChange = useCallback(
    async (quantity: number): Promise<boolean> => {
      try {
        setCount(quantity);
        setProgress(true);
        onUpdateBasket?.();

        let sourceEvent = null;
        if (quantity > productCount) {
          sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM;
        } else if (quantity < productCount) {
          sourceEvent =
            quantity === 0
              ? BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM
              : BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM;
        }

        if (!sourceEvent) return true;

        dispatch(
          updatePreviewItemQuantityAction({
            productId: getProductIdWithoutPrefix(product?.id || ''),
            quantity,
            idToken,
            variantId
          })
        );
        return true;
      } catch (e) {
        console.error(e);
        setCount(basketItem?.quantity || 0);
        return false;
      } finally {
        setProgress(false);
      }
    },
    [
      productCount,
      analyticsData,
      updatePreviewItemQuantityAction,
      variantId,
      basketItem,
      product,
      indexClicked,
      enablePartialQuantity,
      onUpdateBasket
    ]
  );

  const onDelete = useCallback(
    async (e?: SyntheticEvent) => {
      if (e) preventDefault(e);
      if (!basketItem) return;

      setProgress(true);
      try {
        const analyticsToSend = {
          ...analyticsData,
          sourceEvent: BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM,
          triggerType: BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON
        };

        sendCarouselImpressionAnalytics();

        dispatch(
          updatePreviewItemQuantityAction({
            productId: getProductIdWithoutPrefix(product?.id || ''),
            quantity: 0,
            idToken,
            variantId
          })
        );

        const analyticsParams = {
          ...analyticsToSend,
          basketId: basket?.id || 0,
          itemsCount: basket?.basketItems.length || 0,
          priceBeforeDiscount: basket?.prices?.totalOriginalPrice || 0,
          total: basket?.prices?.totalFinalPriceToPay || 0,
          discount: basket?.prices?.roundedDiscountAmount || 0,
          delivery: basket?.prices?.shippingFee || 0,
          storeId: basket?.storeCode || '',
          channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
          segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
          basketSize: basket?.basketItems?.length || 0,
          onePCount: filter1PItems(basket?.basketItems || []).length || 0,
          threePCount: filter3PItems(basket?.basketItems || []).length || 0
        };

        setTimeout(() => {
          if (analyticsToSend) {
            analytics.trackEvent(ANALYTICS.UPDATE_BASKET_ITEM, analyticsParams);
          }
        }, 100);

        setCount(0);
      } catch (error) {
        console.error('Failed to delete item:', error);
      } finally {
        setProgress(false);
        closePicker?.();
      }
    },
    [basketItem, variantId, product, updatePreviewItemQuantityAction, analyticsData, preventDefault, closePicker]
  );

  const onAdd = useCallback(
    async (e: SyntheticEvent) => {
      preventDefault(e);
      setCount(1);

      if (loading) return;

      setProgress(true);
      onUpdateBasket?.();
      sendCarouselImpressionAnalytics();

      const analyticsParams = {
        ...analyticsData,
        channel: ReactNative.isReactNativeWebView() ? SUGGESTEDLIST_CHANNELS.MKP_APP : SUGGESTEDLIST_CHANNELS.MKP_WEB,
        sourceEvent: BASKET_PDP_ATTRIBUTES.BASKET_RECOMMENDATION_V2,
        qty: enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : 1,
        categoryName: categoryAnalytics?.categoryName ?? '',
        ...carouselAnalyticsProps,
        ...productAnalytics(productForHook),
        ...marketingAnalytics(product as Product)
      } as any;

      try {
        dispatch(
          updatePreviewItemQuantityAction({
            productId: getProductIdWithoutPrefix(product?.id || ''),
            quantity: enablePartialQuantity ? AS_HALF_PRODUCT_QUANTITY : 1,
            idToken,
            variantId
          })
        );
      } catch {
        setCount(initialCount);
      } finally {
        setProgress(false);
        setTimeout(() => {
          analytics.trackEvent(ANALYTICS.ADD_TO_BASKET, analyticsParams);
        }, 100);
        SendBrowserEvent.storeBasketItem(storeBasketItemData);
      }
    },
    [
      preventDefault,
      loading,
      product,
      onUpdateBasket,
      categoryAnalytics?.categoryName,
      carouselAnalyticsProps,
      productForHook,
      updatePreviewItemQuantityAction,
      variantId,
      enablePartialQuantity,
      analytics
    ]
  );

  return {
    count,
    inProgress,
    onAdd,
    onIncrement,
    onDecrement,
    onChange,
    onDelete
  };
};
